import React, { memo, useRef, useState, useEffect } from 'react';
import { Modal, Form } from 'antd';
import alert from '@components/alert';
import { Loading, DragableTableFrom as ManageSectionForm } from '@components';
import {
  getProjectSectionColumns,
  getDefaultRowData,
} from '@modules/systemSetting/systemModule/systemModuleColumns';
import { v4 as uuidv4 } from 'uuid';
import { projectHomeAddModalConfig } from '@model/settings/addModalConfig';
import { filterXSS } from 'xss';

const DragModal = (props) => {
  const {
    saveMultiSection,
    moduleValue,
    moduleId,
    order: orderInProps = [],
    names = {},
    modalLoading,
    getAccessibleModules,
    ...restProps
  } = props;

  const [order, setOrder] = useState([]);
  const inialOrder = useRef(orderInProps);
  const [canSave, setCanSave] = useState(true);
  const [loading, setLoading] = useState(false);
  const containerref = useRef(null);
  const [sectionForm] = Form.useForm();

  const fieldMethods = useRef({});

  const registerFieldsMethods = (methods) => {
    fieldMethods.current = methods;
  };

  useEffect(() => {
    const hasError = order.find((item) => item.hasError);
    setCanSave(!hasError);
  }, [order]);

  const handleSortEnd = ({ newIndex, oldIndex }) => {
    const { move } = fieldMethods.current;

    if (move) {
      move(oldIndex, newIndex);
    }
  };
  const getFormData = async () => {
    let sectionFormData;

    try {
      sectionFormData = await sectionForm.validateFields();
    } catch (e) {
      const error =
        e.errorFields && e.errorFields[0] && e.errorFields[0].errors[0];

      alert.warning(error);
      sectionForm.scrollToField(e.errorFields[0].name, { inline: 'start' });
      // scrollYSync();

      return null;
    }
    return sectionFormData?.projectHomeTmplt;
  };

  const handleOk = async () => {
    setLoading(true);

    const formData = await getFormData();
    if (!formData) {
      setLoading(false);
      return;
    }

    // console.log({ formData });

    const data = formData.map((e, i) => {
      const result = {
        id: e.id ? e.id : uuidv4(),
        moduleId,
        // identifier: e.identifier,
        titleName: e.section,
        type: e.type,
        link:
          e.type !== 'pdf'
            ? e.link
            : (e.link || '').replace('/project/pdfModule/', ''),
        display: e.display,
        displayOrder: i + 1,
        ratio: e.ratio,
      };

      if (!result.ratio) {
        delete result.ratio;
      }
      return result;
    });

    await saveMultiSection(data);
    await getAccessibleModules();
    setLoading(false);
    restProps.onCancel();
  };

  const resetForm = () => {
    const order = [...orderInProps];
    setOrder(order);
  };

  const resetToInial = () => {
    const projectHomeTmplt = (inialOrder.current || []).map((e) => ({
      ...e,
      section: e.titleName,
      display: e.display,
      type: e.type,
      link: e.link,
      removeFlag: false,
      hideFlag: false,
    }));
    sectionForm.setFieldsValue({
      projectHomeTmplt,
    });
  };

  const handleCancel = () => {
    restProps.onCancel();
    resetToInial();
  };

  useEffect(() => {
    resetForm();
    const projectHomeTmplt = (orderInProps || []).map((e) => ({
      ...e,
      section: e.titleName,
      display: e.display,
      type: e.type,
      link: e.link,
      removeFlag: false,
      hideFlag: false,
    }));
    sectionForm.setFieldsValue({
      projectHomeTmplt,
    });
    // console.log({ projectHomeTmplt });
    inialOrder.current = orderInProps;
  }, [JSON.stringify(orderInProps)]);

  // console.log({ orderInProps });
  // console.log({ restProps });
  return (
    <Modal
      {...restProps}
      forceRender
      okText="save"
      okButtonProps={{ disabled: !canSave }}
      cancelText="cancel"
      className="projecthome-sort-modal"
      closeIcon={<span className="appkiticon icon-close-fill" />}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={loading}
      wrapClassName="project-home-sorter"
      maskClosable={false}
    >
      <h3 className="title">Customize Sections</h3>

      <div ref={containerref} className="draggable-table-container">
        {modalLoading ? (
          <Loading />
        ) : (
          <>
            <div className="extra-form-in-project-home">
              <Form
                form={sectionForm}
                // {...formLayout}
                className="integration-manage-form customize-project-home-form"
                scrollToFirstError
                autoComplete="off"
              >
                <div className="dragable-table-form">
                  <ManageSectionForm
                    columns={getProjectSectionColumns(false, true)}
                    registerFieldsMethods={registerFieldsMethods}
                    onSortEnd={handleSortEnd}
                    isLoading={false}
                    formInstance={sectionForm}
                    defaultColumns={getProjectSectionColumns(true, true)}
                    name="projectHomeTmplt"
                    isActive={true}
                    getDefaultRowData={getDefaultRowData}
                    addBtnText="Add New Section"
                    configs={{ addModuleConfig: projectHomeAddModalConfig }}
                    showSpn
                  />
                </div>
              </Form>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

const focusOnSectionAndShowEditModal = (section) => {
  const matchingElement = document.evaluate(
    filterXSS(
      `//form[@class='ant-form ant-form-horizontal integration-manage-form customize-project-home-form']//input[@value='${section?.titleName}']`,
    ),
    document,
    null,
    XPathResult.FIRST_ORDERED_NODE_TYPE,
    null,
  ).singleNodeValue;

  if (!matchingElement?.focus) {
    return;
  }

  matchingElement.focus();

  if (!['image', 'pdf'].includes(section.type)) {
    return;
  }

  const editLink = matchingElement
    .closest('.dragable-table-form-row ')
    .querySelector('.upload-pdf-text');

  editLink.click();
};

export default memo((props) => {
  const {
    isModalShow,
    toggleModalShow,
    saveMultiSection,
    activeSection,
    clearActiveSection,
  } = props;

  useEffect(() => {
    if (!activeSection) {
      return;
    }
    try {
      focusOnSectionAndShowEditModal(activeSection);
    } catch (e) {
      // do nothing;
    } finally {
      clearActiveSection();
    }
  }, [activeSection]);

  return (
    <>
      <div
        className="setting-btn"
        size="sm"
        onClick={() => {
          props.refresher(!props.refresh);
          toggleModalShow(true);
        }}
      >
        <span className="appkiticon icon-edit-fill" /> Customize Sections
      </div>

      <DragModal
        visible={isModalShow}
        onCancel={() => toggleModalShow(false)}
        saveMultiSection={saveMultiSection}
        {...props}
      />
    </>
  );
});
